export const FILE_DRAG_TYPE = 'application/x-cfos-file'
export const EXTRA_FOLDERS_STORAGE_KEY = 'cfos.virtualFolders'

export interface FolderNode {
  name: string
  path: string
  children: FolderNode[]
}

type MutableFolderNode = Omit<FolderNode, 'children'> & {
  children: Map<string, MutableFolderNode>
}

export interface FileDragItem {
  fileId: string
  pathId: string
  sourcePath: string
}

export type FileDragData = FileDragItem[]

/** Match backend PathIn normalization: absolute path segments, case-insensitive. */
export function normalizeVirtualPath(path: string): string {
  const parts = path
    .replace(/\\/g, '/')
    .split('/')
    .filter((part) => part && part !== '.')
    .map((part) => part.toLocaleLowerCase())
  return parts.length ? `/${parts.join('/')}` : '/'
}

export function pathsEqual(left: string, right: string): boolean {
  return normalizeVirtualPath(left) === normalizeVirtualPath(right)
}

export function joinVirtualPath(parent: string, name: string): string | null {
  const cleanName = name.trim()
  if (!cleanName || /[/\\]/.test(cleanName)) return null
  const base = normalizeVirtualPath(parent)
  return normalizeVirtualPath(base === '/' ? `/${cleanName}` : `${base}/${cleanName}`)
}

/** Every folder in the path chain, excluding workspace root. */
export function knownFolderPaths(paths: Iterable<string>): Set<string> {
  const known = new Set<string>()
  for (const raw of paths) {
    const normalized = normalizeVirtualPath(raw)
    if (normalized === '/') continue
    const parts = normalized.split('/').filter(Boolean)
    let current = ''
    for (const part of parts) {
      current += `/${part}`
      known.add(current)
    }
  }
  return known
}

export function createVirtualFolder(
  parent: string,
  name: string,
  existingPaths: Iterable<string>,
): { path: string } | { error: string } {
  const trimmed = name.trim()
  if (!trimmed || /[/\\]/.test(trimmed)) {
    return { error: 'Enter a folder name without slashes.' }
  }
  const nextPath = joinVirtualPath(parent, trimmed)
  if (!nextPath) return { error: 'Enter a folder name without slashes.' }
  if (knownFolderPaths(existingPaths).has(nextPath)) {
    return { error: 'That folder already exists.' }
  }
  return { path: nextPath }
}

export function parentVirtualPath(path: string): string {
  const parts = normalizeVirtualPath(path).split('/').filter(Boolean)
  if (parts.length <= 1) return '/'
  return `/${parts.slice(0, -1).join('/')}`
}

export function folderNameFromPath(path: string): string {
  const parts = normalizeVirtualPath(path).split('/').filter(Boolean)
  return parts[parts.length - 1] || ''
}

export function remapDescendantPath(path: string, oldPrefix: string, newPrefix: string): string {
  const current = normalizeVirtualPath(path)
  const from = normalizeVirtualPath(oldPrefix)
  const to = normalizeVirtualPath(newPrefix)
  if (current === from) return to
  if (from !== '/' && current.startsWith(`${from}/`)) {
    return `${to}${current.slice(from.length)}`
  }
  return current
}

export function renameVirtualFolder(
  oldPath: string,
  name: string,
  existingPaths: Iterable<string>,
): { path: string } | { error: string } {
  const source = normalizeVirtualPath(oldPath)
  if (source === '/') return { error: 'Cannot rename workspace root.' }
  const next = createVirtualFolder(parentVirtualPath(source), name, [])
  if ('error' in next) return next
  if (next.path === source) return next
  const known = knownFolderPaths(existingPaths)
  for (const existing of known) {
    if (existing === source || existing.startsWith(`${source}/`)) continue
    if (existing === next.path || existing.startsWith(`${next.path}/`)) {
      return { error: 'That folder already exists.' }
    }
  }
  return next
}

/** Destination for the upload form, including workspace root (`/`). */
export function resolveUploadDestination(raw: string | null | undefined, fallback = '/'): string {
  if (!raw) return fallback
  return normalizeVirtualPath(raw)
}

/** Join a parent folder with a relative directory that may contain slashes (dropped trees). */
export function joinVirtualPathTree(parent: string, relativeDir: string): string | null {
  const parts = relativeDir
    .replace(/\\/g, '/')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part && part !== '.')
  if (parts.some((part) => part === '..')) return null
  if (!parts.length) return normalizeVirtualPath(parent)
  let current = normalizeVirtualPath(parent)
  for (const part of parts) {
    const next = joinVirtualPath(current, part)
    if (!next) return null
    current = next
  }
  return current
}

export function buildFolderTree(paths: string[]): FolderNode[] {
  const roots = new Map<string, MutableFolderNode>()
  for (const rawPath of new Set(paths.map(normalizeVirtualPath).filter((path) => path !== '/'))) {
    const parts = rawPath.split('/').filter(Boolean)
    let children = roots
    let currentPath = ''
    for (const part of parts) {
      currentPath += `/${part}`
      let node = children.get(part)
      if (!node) {
        node = { name: part, path: currentPath, children: new Map() }
        children.set(part, node)
      }
      children = node.children
    }
  }
  const materialize = (nodes: Map<string, MutableFolderNode>): FolderNode[] =>
    [...nodes.values()]
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((node) => ({
        name: node.name,
        path: node.path,
        children: materialize(node.children),
      }))
  return materialize(roots)
}

export function foldersAtPath(tree: FolderNode[], path: string): FolderNode[] {
  const current = normalizeVirtualPath(path)
  if (current === '/') return tree
  const parts = current.split('/').filter(Boolean)
  let nodes = tree
  let match: FolderNode | undefined
  for (const part of parts) {
    match = nodes.find((node) => node.name === part)
    if (!match) return []
    nodes = match.children
  }
  return match?.children ?? []
}

/** Ancestor chain for breadcrumb navigation (excludes root). */
export function breadcrumbSegments(path: string): { name: string; path: string }[] {
  const parts = normalizeVirtualPath(path).split('/').filter(Boolean)
  const segments: { name: string; path: string }[] = []
  let current = ''
  for (const part of parts) {
    current += `/${part}`
    segments.push({ name: part, path: current })
  }
  return segments
}

/** Parse a typed or pasted address-bar path into a normalized virtual folder. */
export function parseTypedVirtualPath(raw: string): string {
  let value = raw.trim()
  if (value.length >= 2) {
    const quote = value[0]
    if ((quote === '"' || quote === "'") && value.endsWith(quote)) {
      value = value.slice(1, -1).trim()
    }
  }
  const alias = value.replace(/\\/g, '/').replace(/\/+$/, '').toLocaleLowerCase()
  if (!alias || alias === 'root' || alias === 'workspace' || alias === 'all files') return '/'

  const resolved: string[] = []
  for (const part of value.replace(/\\/g, '/').split('/').map((item) => item.trim())) {
    if (!part || part === '.') continue
    if (part === '..') {
      resolved.pop()
      continue
    }
    resolved.push(part.toLocaleLowerCase())
  }
  return resolved.length ? `/${resolved.join('/')}` : '/'
}

export function loadExtraFolders(storage: Pick<Storage, 'getItem'> = localStorage): string[] {
  try {
    const raw = storage.getItem(EXTRA_FOLDERS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return [...new Set(parsed.filter((item): item is string => typeof item === 'string').map(normalizeVirtualPath).filter((path) => path !== '/'))]
  } catch {
    return []
  }
}

export function saveExtraFolders(paths: string[], storage: Pick<Storage, 'setItem'> = localStorage) {
  const normalized = [...new Set(paths.map(normalizeVirtualPath).filter((path) => path !== '/'))].sort()
  storage.setItem(EXTRA_FOLDERS_STORAGE_KEY, JSON.stringify(normalized))
  return normalized
}

function isFileDragItem(value: unknown): value is FileDragItem {
  if (!value || typeof value !== 'object') return false
  const item = value as Partial<FileDragItem>
  return typeof item.fileId === 'string' && typeof item.pathId === 'string' && typeof item.sourcePath === 'string'
}

export function encodeFileDrag(data: FileDragData) {
  return JSON.stringify(data)
}

export function decodeFileDrag(value: string): FileDragData | null {
  try {
    const parsed = JSON.parse(value) as unknown
    if (Array.isArray(parsed)) {
      const items = parsed.filter(isFileDragItem)
      return items.length ? items : null
    }
    return isFileDragItem(parsed) ? [parsed] : null
  } catch {
    return null
  }
}
